<template>
  <!-- The only gate between an ad and a paying user. Every ad unit on the site
       goes through this component so that rule lives in one place. -->
  <div v-if="showAd" class="ads-container" :class="`ads-container--${adsType}`">
    <span class="ads-label">Advertisement</span>
    <div class="ads-div">
      <!--
        Responsive, not a fixed size. The container used to be a 50%-wide,
        80px-tall grid cell, which no standard creative fits: a 728x90 needs
        90px of height and a 320x50 needs 320px of width, so Google mostly
        returned nothing. With the full width available the auction picks
        whichever size pays most - see adFormat for which shapes are allowed.

        Not rendered until the box is near the screen (see watchVisibility).
      -->
      <Adsense
        v-if="nearViewport"
        ins-class="amf-ad"
        :data-ad-format="adFormat"
        data-full-width-responsive="yes"
        :data-ad-client="adClient"
        :data-ad-slot="adSlot">
      </Adsense>
    </div>
  </div>
</template>

<script>
const AD_CLIENT = 'ca-pub-1569923671775194';

// One AdSense unit per placement, so each one's earnings show separately.
const AD_SLOTS = {
  header: '1776819316', // home-header: between the faves bar and The Latest
  top: '5460045048', // first unit between the rows
  middle: '1991994626', // home-mid-2: second unit between the rows
  bottom: '3163423883', // above the footer
};

// Placements that sit between content the reader came to use. On desktop
// they are held to a banner shape; see adFormat.
const BANNER_ON_DESKTOP = ['header', 'top', 'middle'];

// How far outside the screen a unit starts loading. Far enough that the
// creative is usually there by the time the box scrolls in, near enough that
// a unit nobody reaches is never requested.
const LOAD_MARGIN = '800px 0px';

// How long a box has to stay within that margin before it is requested.
// Without it the unit above the footer loaded on every visit: App.vue mounts
// it before the rows arrive from the API, when it is still near the top of an
// empty page. The rows push it away inside this window and the timer resets.
// It also skips units a reader scrolls straight past.
const LOAD_DWELL_MS = 1500;

export default {
  name: 'RecAds',
  props: {
    adsType: {
      type: String,
      required: true,
    },
    // For placements that would push a phone's first screen of logos out of
    // view, such as the one above The Latest.
    desktopOnly: Boolean,
    HasNotAdsSection: Boolean,
    hasSubscription: Boolean,
  },
  data() {
    return {
      // Read once: Google fixes a unit's shape at request time, so following
      // a later resize would change nothing already served.
      isPhone: typeof window !== 'undefined'
        && window.matchMedia('(max-width: 767px)').matches,
      nearViewport: false,
      observer: null,
      dwellTimer: null,
    };
  },
  computed: {
    showAd() {
      return !this.HasNotAdsSection && !this.hasSubscription
        && !(this.desktopOnly && this.isPhone);
    },
    adClient() {
      return AD_CLIENT;
    },
    // Computed rather than assigned in mounted(): a child mounts before its
    // parent, so <Adsense> would render its <ins> before a slot was set.
    adSlot() {
      return AD_SLOTS[this.adsType] || '';
    },
    // Between the rows on desktop the ad is held to a banner shape (728x90 /
    // 970x90). Left on "auto", Google sizes it ~280px tall there, the height
    // of five or six rows. Phones and the unit above the footer stay on
    // "auto", where 300x250 / 336x280 - the better-paying sizes - fit.
    adFormat() {
      return !this.isPhone && BANNER_ON_DESKTOP.includes(this.adsType) ? 'horizontal' : 'auto';
    },
  },
  watch: {
    // The user can arrive after mount (a stored session refreshed from the
    // API), which flips the gate; start or stop watching to match.
    showAd(show) {
      if (show) {
        this.$nextTick(this.watchVisibility);
      } else {
        this.stopWatching();
      }
    },
  },
  mounted() {
    this.watchVisibility();
  },
  beforeDestroy() {
    this.stopWatching();
  },
  methods: {
    /**
     * Request the ad only once its box is within LOAD_MARGIN of the screen.
     *
     * A unit requested at page load but never scrolled to still counts as
     * served and never seen. Google weighs that when deciding whether a slot
     * is worth filling at all - the unit above the footer came back
     * "unfill-optimized" on its first day for exactly this reason.
     */
    watchVisibility() {
      if (!this.showAd || this.nearViewport || this.observer) return;
      // With the gate closed $el is a comment node, which cannot be observed.
      if (!this.$el || this.$el.nodeType !== 1) return;

      if (typeof IntersectionObserver === 'undefined') {
        this.nearViewport = true;
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        const near = entries[entries.length - 1].isIntersecting;
        if (!near) {
          clearTimeout(this.dwellTimer);
          this.dwellTimer = null;
          return;
        }
        if (this.dwellTimer) return;
        this.dwellTimer = setTimeout(() => {
          this.dwellTimer = null;
          this.nearViewport = true;
          this.stopWatching();
        }, LOAD_DWELL_MS);
      }, { rootMargin: LOAD_MARGIN });
      this.observer.observe(this.$el);
    },
    stopWatching() {
      clearTimeout(this.dwellTimer);
      this.dwellTimer = null;
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },
  },
};
</script>

<style lang="scss">
  @import '@/assets/scss/layout/rec-ads.scss';
</style>
